import { styled } from '@volkswagen-onehub/components-core';
import React, {
  createContext,
  MutableRefObject,
  PropsWithChildren,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

const AppWrapper = styled.div``;

interface ScrollContextInterface {
  appContainer?: HTMLElement;
  bannerHeight?: number;
  atTop: boolean;
  atBottom: boolean;
  scrollToTop?: (elemFocus?: HTMLElement) => void;
  activeColumns: number[];
  activateColumns?: (pos: number) => void;
  appRef: MutableRefObject<HTMLDivElement | null> | null;
}

export const ScrollContext = createContext<ScrollContextInterface>({
  atTop: true,
  atBottom: false,
  activeColumns: [],
  appRef: null
});

export const ScrollContextProvider = ({children}: PropsWithChildren) => {

  const appRef = useRef<HTMLDivElement | null>(null);

  const [ appContainer, setAppContainer ] = useState<HTMLElement>();
  const [ atTop, setAtTop ] = useState(true);
  const [ atBottom, setAtBottom ] = useState(false);
  const [ activeColumns, setActiveColumns ] = useState<number[]>([]);
  const [ bannerHeight, setBannerHeight ] = useState(0);

  const activateColumns = (pos: number) => {
    const index = activeColumns.indexOf(pos);
    let arr;
    if (index > -1) {
      arr = [...activeColumns.slice(0, index), ...activeColumns.slice(index)];
    } else {
      arr = [...activeColumns, pos];
    }
    setActiveColumns(arr);
  };

  const scrollToTop = (elemFocus?: HTMLElement) => {
    window.scrollTo(0, 0);
    document.documentElement.scrollTop = 0;
    if (elemFocus) {
      elemFocus.focus();
    } else {
      appContainer?.focus();
    }
  };

  const handleScroll = () => {
    if (window.scrollY > window.innerHeight * 0.25) {
      setAtTop(false);
    } else {
      setAtTop(true);
    }

    const yPosition = window.scrollY + window.innerHeight;
    const yTrigger =  (appContainer?.offsetTop || 0) + (appContainer?.clientHeight || 0) + 122;
    if (yPosition > yTrigger) {
      setAtBottom(true);
    } else {
      setAtBottom(false);
    }
  };

  const getBannerHeight = () => {
    const height = (document.querySelectorAll('[role="banner"]').item(0) as HTMLElement)?.offsetHeight || 0;
    setBannerHeight(height);
  };

  useEffect(() => {
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [handleScroll]);

  useEffect(() => {
    window.addEventListener("resize", getBannerHeight);
    return () => window.removeEventListener("resize", getBannerHeight);
  }, [getBannerHeight]);

  useEffect(() => {
    setAppContainer(document.getElementById("compare-tool") as HTMLElement);
    setTimeout(() => {
      getBannerHeight();
    }, 500);
  }, []);

  const value = useMemo(() => ({
    appContainer,
    bannerHeight,
    atTop,
    atBottom,
    activeColumns,
    scrollToTop,
    activateColumns,
    appRef
  }), [
    appContainer,
    bannerHeight,
    atTop,
    atBottom,
    activeColumns,
    scrollToTop,
    activateColumns,
    appRef
  ]);

  return (
    <AppWrapper ref={appRef}>
      <ScrollContext.Provider value={value}>
        {children}
      </ScrollContext.Provider>
    </AppWrapper>
  );
};
